/**
 * APB-1600: BRAIN Assistant API client.
 *
 * Mirrors the axios pattern used in alertApi.ts / auditApi.ts:
 *   - apiClientWithRetry for HTTP calls
 *   - getFullApiUrl() for tenant-aware base URL
 *   - tenant + auth headers already attached via global interceptors
 *
 * v1 is non-streaming. Phase 4 adds SSE — see FINISH_LIST.md.
 */
import { apiClientWithRetry } from '../shared/services/apiClient.js';
import { getFullApiUrl } from '../shared/utils/apiConfig';

// Base URL for API requests with tenant-aware routing (evaluated per-call to avoid stale values)
const getApiBaseUrl = () => getFullApiUrl();

// ---- Wire shapes (match src/api/assistant_routes.py) ---------------------

export type AssistantRole = 'admin' | 'executive' | 'analyst';

export interface Citation {
  /** "alert" | "entity" | "ioc" | "threat_actor" */
  type: string;
  /** ID or indicator value depending on type */
  id: string;
}

export interface ChatRequestBody {
  message: string;
  session_id?: string | null;
  /** Optional override; server derives from JWT if omitted */
  role?: AssistantRole;
}

export interface ChatResponseBody {
  session_id: string;
  content: string;
  citations: Citation[];
  finish_reason: string;
  turns: number;
  tool_calls_made: number;
  prompt_tokens: number;
  completion_tokens: number;
}

export interface SessionHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface SessionHistoryResponse {
  session_id: string;
  tenant_id: string;
  messages: SessionHistoryMessage[];
  created_at: string;
  last_activity: string;
}

// ---- Calls ---------------------------------------------------------------

/**
 * POST /api/assistant/chat
 *
 * One user turn. Returns the final assistant answer + citations.
 * The orchestrator handles the tool-calling loop server-side.
 */
export async function sendChatMessage(
  body: ChatRequestBody,
): Promise<ChatResponseBody> {
  const res = await apiClientWithRetry.post<ChatResponseBody>(
    `${getApiBaseUrl()}/api/assistant/chat`,
    body,
  );
  return res.data;
}

/**
 * GET /api/assistant/sessions/:id
 *
 * Fetch conversation history for resume scenarios (user refreshes the
 * page mid-conversation). Returns 404 if session expired or belongs to
 * another tenant.
 */
export async function getSessionHistory(
  sessionId: string,
): Promise<SessionHistoryResponse> {
  const res = await apiClientWithRetry.get<SessionHistoryResponse>(
    `${getApiBaseUrl()}/api/assistant/sessions/${encodeURIComponent(sessionId)}`,
  );
  return res.data;
}

/**
 * DELETE /api/assistant/sessions/:id
 *
 * Explicit "start over" button handler. Idempotent — server returns
 * 204 regardless of whether the session existed.
 */
export async function endSession(sessionId: string): Promise<void> {
  await apiClientWithRetry.delete(
    `${getApiBaseUrl()}/api/assistant/sessions/${encodeURIComponent(sessionId)}`,
  );
}
